import * as Yup from 'yup';
import { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import {
    Card, Stack, Container, Typography, TextField, IconButton, InputAdornment,
    Alert, Box, Link, Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import Page from 'src/components/Page';
import syndicationApi from 'src/utils/syndicationApi';

const RootStyle = styled(Page)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    padding: theme.spacing(4, 0),
}));

const FormCard = styled(Card)(({ theme }) => ({
    maxWidth: 480,
    width: '100%',
    padding: theme.spacing(5),
    borderRadius: 16,
    boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
}));

export default function InvestorResetPassword() {
    const { token } = useParams();
    const [showPassword, setShowPassword] = useState(false);
    const [needsTotp, setNeedsTotp] = useState(false);
    const [apiError, setApiError] = useState('');
    const [success, setSuccess] = useState(false);

    const ResetSchema = Yup.object().shape({
        new_password: Yup.string()
            .min(8, 'Minimum 8 characters')
            .matches(/[A-Z]/, 'Must contain an uppercase letter')
            .matches(/[a-z]/, 'Must contain a lowercase letter')
            .matches(/[0-9]/, 'Must contain a digit')
            .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Must contain a special character')
            .required('New password is required'),
        totp_code: Yup.string().when([], {
            is: () => needsTotp,
            then: Yup.string().matches(/^[0-9]{6}$/, 'Must be 6 digits').required('2FA code is required'),
            otherwise: Yup.string(),
        }),
    });

    const formik = useFormik({
        initialValues: { new_password: '', totp_code: '' },
        validationSchema: ResetSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setApiError('');
            try {
                await syndicationApi.post('investor/reset-password/', {
                    token,
                    new_password: values.new_password,
                    totp_code: values.totp_code || '',
                });
                setSuccess(true);
            } catch (error) {
                const resp = error.response?.data;
                if (resp?.code === 'TOTP_REQUIRED') {
                    setNeedsTotp(true);
                    setApiError('This account has 2FA enabled. Please enter your authenticator code.');
                } else {
                    setApiError(resp?.message || 'Reset failed. The link may be expired.');
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    if (success) {
        return (
            <RootStyle title="Password Reset">
                <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                    <FormCard>
                        <Stack spacing={3} alignItems="center" sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" color="primary">Password Reset!</Typography>
                            <Alert severity="success" sx={{ width: '100%' }}>
                                Your password has been reset successfully. You can now log in.
                            </Alert>
                            <Link component={RouterLink} to="/investor/login" variant="subtitle1">
                                Go to Login
                            </Link>
                        </Stack>
                    </FormCard>
                </Container>
            </RootStyle>
        );
    }

    return (
        <RootStyle title="Reset Password | Gateway X Syndication">
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                <FormCard>
                    <Stack spacing={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" gutterBottom>Reset Password</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Enter your new password below.
                            </Typography>
                        </Box>

                        {apiError && <Alert severity="error">{apiError}</Alert>}

                        <FormikProvider value={formik}>
                            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                                <Stack spacing={2.5}>
                                    <TextField
                                        fullWidth
                                        label="New Password"
                                        type={showPassword ? 'text' : 'password'}
                                        {...formik.getFieldProps('new_password')}
                                        error={Boolean(formik.touched.new_password && formik.errors.new_password)}
                                        helperText={formik.touched.new_password && formik.errors.new_password}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    {needsTotp && (
                                        <TextField
                                            fullWidth
                                            label="2FA Code"
                                            placeholder="000000"
                                            inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 20, letterSpacing: 6 } }}
                                            {...formik.getFieldProps('totp_code')}
                                            error={Boolean(formik.touched.totp_code && formik.errors.totp_code)}
                                            helperText={formik.touched.totp_code && formik.errors.totp_code}
                                        />
                                    )}

                                    <LoadingButton
                                        fullWidth
                                        size="large"
                                        type="submit"
                                        variant="contained"
                                        loading={formik.isSubmitting}
                                    >
                                        Reset Password
                                    </LoadingButton>
                                </Stack>
                            </Form>
                        </FormikProvider>

                        <Divider />
                        <Link component={RouterLink} to="/investor/login" variant="body2" sx={{ textAlign: 'center' }}>
                            Back to Login
                        </Link>
                    </Stack>
                </FormCard>
            </Container>
        </RootStyle>
    );
}
